import type {
  Browser,
  BrowserConfigEntry,
  ExtensionConfig,
  ExtensionRequest,
  ExtensionResponse,
  ExtensionResult,
  ExtensionStatus,
} from "./client.types"

// An extension that never answers leaves the message open until Chrome closes it, minutes later.
const RESPONSE_TIMEOUT_MS = 10_000

export class ExtensionConnection {
  static status: ExtensionStatus = "uninitialized"
  static extensionId: string | null = null
  // The in-flight attempt to connect. Whoever needs the resolved extensionId
  // awaits this; it settles to a final state — it does NOT wait for the extension to actually be present.
  static connectionAttempt: Promise<void> | null = null
  static #browserConfig: BrowserConfigEntry | null = null
  static #subscribers = new Set<() => void>()
  static #isConnecting = false

  static setup(config: ExtensionConfig): void {
    const browser = this.#detectBrowser()
    const browserConfig = browser && config[browser]
    if (!browserConfig) {
      this.status = "unsupported"
      // Notified after setup returns, as a finished check is: the controllers registered after
      // this one (the popups a subscriber opens) aren't connected yet.
      queueMicrotask(() => this.#subscribers.forEach((cb) => cb()))
      return
    }

    this.#browserConfig = browserConfig
    window.addEventListener("focus", this.#onFocus)
    this.reconnect()
  }

  static get storeUrl(): string | null {
    return this.#browserConfig?.storeUrl ?? null
  }

  static get browser(): Browser | null {
    return this.#browserConfig?.browser ?? null
  }

  static onChange(callback: () => void): () => void {
    this.#subscribers.add(callback)
    return () => this.#subscribers.delete(callback)
  }

  // Re-resolve the connection (the user may have installed or removed the
  // extension while the page was open). Notifies subscribers once settled so
  // they re-sync anything derived from the extension.
  static reconnect(): void {
    if (!this.#browserConfig || this.#isConnecting) return

    this.#isConnecting = true
    this.connectionAttempt = this.#resolveExtension(this.#browserConfig)
    this.connectionAttempt
      .then(() => this.#subscribers.forEach((cb) => cb()))
      .finally(() => {
        this.#isConnecting = false
      })
  }

  static #onFocus = (): void => this.reconnect()

  static async sendMessage<ResponseData>(
    message: ExtensionRequest,
  ): Promise<ExtensionResult<ResponseData>> {
    await this.connectionAttempt
    if (!this.extensionId) return { success: false, reason: "unavailable" }

    return this.#dispatch<ResponseData>(this.extensionId, message)
  }

  static async #resolveExtension(config: BrowserConfigEntry): Promise<void> {
    if (await this.#pingExtension(config.productionId)) {
      this.status = "connected"
      this.extensionId = config.productionId
      return
    }

    if (config.devMode && config.devId && (await this.#pingExtension(config.devId))) {
      this.status = "connected"
      this.extensionId = config.devId
      return
    }

    this.status = "not_connected"
    this.extensionId = null
  }

  static #detectBrowser(): Browser | undefined {
    if (navigator.userAgent.includes("Edg/")) return "edge"
    if (navigator.userAgent.includes("Chrome/")) return "chrome"
  }

  static async #pingExtension(extensionId: string): Promise<boolean> {
    const result = await this.#dispatch<"pong">(extensionId, { action: "ping" })
    return result.success && result.data === "pong"
  }

  // Raw message to a specific extension id — the only place that touches chrome.
  // #pingExtension uses it during the attempt, so it must NOT await the attempt.
  static #dispatch<ResponseData>(
    extensionId: string,
    message: ExtensionRequest,
  ): Promise<ExtensionResult<ResponseData>> {
    if (typeof chrome === "undefined" || !chrome.runtime?.sendMessage) {
      return Promise.resolve({ success: false, reason: "unavailable" })
    }

    return new Promise((resolve) => {
      // Promise settles once, so a callback that arrives after the timeout changes nothing.
      const timeout = setTimeout(
        () => resolve({ success: false, reason: "no_response" }),
        RESPONSE_TIMEOUT_MS,
      )

      try {
        chrome.runtime.sendMessage<ExtensionRequest, ExtensionResponse<ResponseData>>(
          extensionId,
          message,
          (response) => {
            clearTimeout(timeout)
            if (chrome.runtime.lastError) return resolve({ success: false, reason: "no_response" })

            if (response.success) {
              resolve({ success: true, data: response.data })
            } else {
              resolve({ success: false, reason: "error", errors: response.errors })
            }
          },
        )
      } catch {
        clearTimeout(timeout)
        resolve({ success: false, reason: "error" })
      }
    })
  }
}
